import { useTranslation } from 'react-i18next';
import type { Namespace } from './index';

/**
 * Custom translation hook that provides a cleaner API.
 * 
 * Two namespaces available:
 * - 'app' (default): Main app translations (common, auth, booking, chat, profile)
 * - 'landing': Landing page translations
 * 
 * @example
 * // Using default namespace (app)
 * const { t } = useT();
 * t('common.buttons.login'); // From app.json
 * t('dashboard.welcomeBack', { name: 'John' }); // "Welcome back, John! 👋"
 * 
 * @example
 * // Using landing namespace
 * const { t } = useT('landing');
 * t('hero.title'); // From landing.json
 * t('app:common.buttons.learnMore'); // Cross-namespace access to app.json
 */
export function useT(ns?: Namespace | Namespace[]) {
  const { t, i18n, ready } = useTranslation(ns);
  
  return {
    /**
     * Translate a key. Supports interpolation and pluralization.
     * 
     * @example
     * t('buttons.login') // Simple key
     * t('messages.copiedToClipboard', { label: 'Email' }) // With interpolation
     * t('booking:event.spotsLeft', { count: 3 }) // With pluralization
     */
    t,
    
    /**
     * i18n instance for advanced operations like language switching
     */
    i18n,
    
    /**
     * Whether translations are loaded and ready
     */
    ready,
    
    /**
     * Current language code (e.g., 'en', 'es')
     */
    language: i18n.language,
    
    /**
     * Change the current language
     * 
     * @example
     * changeLanguage('es');
     */
    changeLanguage: i18n.changeLanguage.bind(i18n),
  };
}

export default useT;
